"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import Icon, { type IconName } from "./Icon";
import { adminApi, type AdminDeal, type PageMeta } from "../lib/api";
import { useApi } from "../lib/useApi";

interface Cmd {
  id: string;
  label: string;
  hint?: string;
  icon: IconName;
  href: string;
}

const pages: Cmd[] = [
  { id: "p-overview", label: "Overview", icon: "grid", href: "/" },
  { id: "p-bells", label: "Live Bells", icon: "bell", href: "/bells" },
  { id: "p-deals", label: "Deals", icon: "handshake", href: "/deals" },
  { id: "p-managers", label: "Team", icon: "users", href: "/managers" },
  { id: "p-analytics", label: "Analytics", icon: "chart", href: "/analytics" },
  { id: "p-audit", label: "Reports", icon: "download", href: "/audit" },
  { id: "p-settings", label: "Configuration", icon: "settings", href: "/settings" },
];

/** Custom event so the decorative Topbar search box can open this without the
 *  two components sharing a parent — see Topbar.tsx. */
export const OPEN_COMMAND_PALETTE_EVENT = "open-command-palette";

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [active, setActive] = useState(0);
  const router = useRouter();

  // Global ⌘K / Ctrl+K toggle, plus the Topbar search box's open request.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    const onOpenRequest = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpenRequest);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_COMMAND_PALETTE_EVENT, onOpenRequest);
    };
  }, []);

  // Debounce before hitting the deals API (same 250-350ms pattern used by the
  // Deals/Customers/Managers pages).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const dealSearch = useApi<{ items: AdminDeal[]; meta: PageMeta }>(
    (signal) =>
      debouncedQ
        ? adminApi.deals({ search: debouncedQ, limit: 8 }, signal)
        : Promise.resolve({ items: [], meta: {} }),
    [debouncedQ],
    "Deal search failed.",
  );

  const results = useMemo<Cmd[]>(() => {
    const t = q.toLowerCase().trim();
    const matchedPages = t ? pages.filter((c) => c.label.toLowerCase().includes(t)) : pages;
    if (!t) return matchedPages;

    const dealCmds: Cmd[] = (dealSearch.data?.items ?? []).map((d) => ({
      id: `d-${d.id}`,
      label: `${d.customerName ?? "Guest Customer"} · ${d.vehicle ?? "—"}`,
      hint: `#${d.code}`,
      icon: "handshake",
      href: "/deals",
    }));
    return [...matchedPages, ...dealCmds];
  }, [q, dealSearch.data]);

  useEffect(() => setActive(0), [q, open]);

  const go = (c: Cmd) => {
    setOpen(false);
    setQ("");
    router.push(c.href);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[12vh] px-4" onClick={() => setOpen(false)}>
      <div className="overlay-backdrop absolute inset-0" />
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          if (e.key === "Enter" && results[active]) go(results[active]);
        }}
        className="glass modal-pop relative w-[min(94vw,560px)] rounded-[24px] p-3 flex flex-col"
      >
        <div className="neu-inset flex items-center gap-3 px-4 h-12 rounded-2xl text-text-muted">
          <Icon name="search" size={18} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Jump to page or deal…"
            className="bg-transparent outline-none text-sm text-text placeholder:text-text-faint w-full"
          />
          <kbd className="text-[10px] text-text-faint border border-current/20 rounded px-1.5 py-0.5">ESC</kbd>
        </div>

        <ul className="mt-3 max-h-[50vh] overflow-y-auto flex flex-col gap-1">
          {results.length === 0 && (
            <li className="text-center text-[13px] text-text-faint py-8">
              {q.trim() && dealSearch.loading ? "Searching…" : "No matches"}
            </li>
          )}
          {results.map((c, i) => (
            <li key={c.id}>
              <button
                onMouseEnter={() => setActive(i)}
                onClick={() => go(c)}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-2xl text-left transition-colors ${
                  i === active ? "neu-inset text-accent" : "text-text-muted"
                }`}
              >
                <Icon name={c.icon} size={18} />
                <span className="text-[13.5px] font-medium text-text flex-1 truncate">{c.label}</span>
                {c.hint && <span className="text-[12px] text-text-faint">{c.hint}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
